/**
 * Aritmética pura de Mi Cuenta: totales, cupones, ahorro anual, precio por
 * timbre y formato de moneda. Sin DOM, sin jQuery, sin red.
 *
 * `aplicarDescuento` es la GEMELA de Pago_panel::aplicar_descuento() (PHP,
 * `application/libraries/pagos/Pago_panel.php`). Si divergen, la pantalla
 * enseña un total y el cargo es otro — por eso los dos tienen los mismos
 * casos de prueba (incluido el 33 % de 99, donde el redondeo se nota).
 *
 * El servidor sigue siendo la autoridad: estas funciones solo sirven para que
 * la pantalla reaccione al instante mientras el servidor confirma.
 */

/**
 * Redondeo a 2 decimales con paridad exacta con el `round()` de PHP
 * (revisión ronda 1: el truco `+ Number.EPSILON` es un no-op para magnitudes
 * >= 1 y diverge de PHP en casos reales — 149.985 con 33.33 % de 450 daba
 * 149.98 en vez de 149.99 — porque no reproduce el pre-redondeo a 15 dígitos
 * significativos que PHP aplica antes de redondear a los decimales pedidos.
 * Verificado contra PHP en una malla de 322 combinaciones de base/cupón:
 * 0 divergencias con este pre-redondeo, 3 sin él.
 */
function r2(n) {
  var x = Number(n);
  if (!isFinite(x)) { return 0; }
  var s = x < 0 ? -1 : 1;
  var pre = Number(Math.abs(x).toPrecision(15)); // mismo pre-redondeo que round() de PHP
  return s * Number(Math.round(Number(pre + 'e+2')) + 'e-2');
}

/**
 * @param {number} base
 * @param {{tipo: string, valor: number}|null} cupon
 * @returns {{base: number, descuento: number, total: number}}
 */
export function aplicarDescuento(base, cupon) {
  var b = r2(base);
  if (!(b > 0) || !cupon) {
    // Sin clamp a 0: PHP tampoco lo hace (round($base,2) tal cual, aunque sea
    // negativo o cero) — hoy inalcanzable porque el precio siempre sale de
    // admin_modulo, pero la paridad es la paridad.
    return { base: b, descuento: 0, total: b };
  }
  var valor = Number(cupon.valor) || 0;
  var descuento = 0;
  if (cupon.tipo === 'porcentaje') {
    descuento = r2(b * (valor / 100));
  } else if (cupon.tipo === 'monto') {
    descuento = r2(valor);
  }
  if (descuento < 0) { descuento = 0; }
  if (descuento > b) { descuento = b; }
  return { base: b, descuento: descuento, total: r2(b - descuento) };
}

/** Cuánto se ahorra al año pagando anual. Nunca negativo. */
export function ahorroAnual(mensual, anual) {
  var m = Number(mensual) || 0;
  var a = Number(anual) || 0;
  if (m <= 0 || a <= 0) { return 0; }
  var ahorro = r2(m * 12 - a);
  return ahorro > 0 ? ahorro : 0;
}

/** Lo que "cuesta al mes" el plan anual. */
export function mensualEquivalente(anual) {
  var a = Number(anual) || 0;
  return a > 0 ? r2(a / 12) : 0;
}

/**
 * La promesa completa del plan anual, exactamente como la aprobó Adrián:
 * "2 meses gratis · ahorras $900.00". Cadena vacía si no hay ahorro real.
 */
export function etiquetaAhorro(mensual, anual) {
  var ahorro = ahorroAnual(mensual, anual);
  if (ahorro <= 0) { return ''; }
  var meses = Math.round(ahorro / (Number(mensual) || 1));
  var plural = meses === 1 ? 'mes gratis' : 'meses gratis';
  return meses + ' ' + plural + ' · ahorras ' + mx(ahorro);
}

/**
 * El compromiso honesto de la renovación: cuándo se vuelve a cobrar y CUÁNTO.
 *
 * GEMELA de `Cuenta_seccion::texto_renovacion()` (PHP). El servidor pinta la
 * primera frase con el ciclo inicial y esta función la repinta en cuanto el
 * usuario mueve el selector o aplica un cupón; si divergieran, la pantalla
 * cambiaría de promesa sola al primer clic.
 *
 * La regla que no se negocia: un cupón NUNCA puede esconder el precio de la
 * renovación. Se dice lo de hoy Y lo de después, aunque hoy sea $0.
 *
 * @param {string} ciclo      'anual' | 'mensual' (cualquier otro cae a mensual)
 * @param {number} precio     precio de lista del ciclo (admin_modulo)
 * @param {number} totalHoy   lo que de verdad se cobra hoy (con cupón aplicado)
 */
export function textoRenovacion(ciclo, precio, totalHoy) {
  var anual = (ciclo === 'anual');
  var lista = Number(precio) || 0;
  var hoy = Number(totalHoy) || 0;
  var cada = 'Se renueva automáticamente cada ' + (anual ? 'año' : 'mes') + ' por ' + mx(lista) + '.';
  var cancelar = ' Puedes cancelar cuando quieras desde esta pantalla.';

  if (r2(hoy) < r2(lista)) {
    var hoyFrase = (r2(hoy) <= 0)
      ? 'Hoy no pagas nada con tu cupón.'
      : ('Hoy pagas ' + mx(hoy) + ' con tu cupón.');
    return hoyFrase + ' Después ' + cada.charAt(0).toLowerCase() + cada.slice(1) + cancelar;
  }
  return cada + cancelar;
}

/** @returns {number|null} */
export function precioPorTimbre(precio, cantidad) {
  var c = Number(cantidad) || 0;
  if (c <= 0) { return null; }
  return r2(Number(precio) / c);
}

/**
 * "A este ritmo te alcanzan para N días". null cuando no hubo consumo en los
 * últimos 30 días (no hay ritmo del cual proyectar).
 * @returns {number|null}
 */
export function proyeccionDias(saldo, usados30) {
  var u = Number(usados30) || 0;
  if (u <= 0) { return null; }
  var s = Number(saldo) || 0;
  if (s <= 0) { return 0; }
  return Math.floor(s / (u / 30));
}

/** $1,234.56 */
export function mx(n) {
  return '$' + (Number(n) || 0).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** $1,235 — para textos donde los centavos estorban ("equivale a $375 al mes"). */
export function mxEntero(n) {
  return '$' + Math.round(Number(n) || 0).toLocaleString('es-MX');
}

/** Suma las líneas del `desglose` del contrato del servidor. */
export function totalDelDesglose(desglose) {
  if (!Array.isArray(desglose)) { return 0; }
  return r2(desglose.reduce(function (acc, fila) {
    return acc + (Number(fila && fila.monto) || 0);
  }, 0));
}
